import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { CheckCircle, Circle, Loader } from 'lucide-react';
import styles from './JuegoCarrera.module.css';
import {useAuth} from "../../Auth/AuthContext";

const JuegoCarrera = () => {
  const { getToken } = useAuth(); 
  const token = getToken();
  const navigate = useNavigate();
  const [pasos, setPasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [hoveredCell, setHoveredCell] = useState(null);

  const gameConfig = {
    rows: 7,
    cols: 10,
    disabledCells: [1, 2, 3, 8, 17, 18, 19, 26, 29, 30, 36, 37, 54, 49, 54, 59, 60, 62, 65, 66, 67, 68, 69]
  };

  const getUserId = () => {
      if (token) {
          try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              return payload.id;
          } catch (error) {
              console.error('Error al decodificar token:', error);
              return null;
          }
      }
      return null;
  };

  // Función para calcular posiciones disponibles (excluyendo las deshabilitadas)
  const getAvailablePositions = () => {
    const totalPositions = gameConfig.rows * gameConfig.cols; // 70 posiciones
    const availablePositions = [];
    
    for (let i = 0; i < totalPositions; i++) {
      if (!gameConfig.disabledCells.includes(i)) {
        availablePositions.push(i);
      }
    }
    
    return availablePositions; // Esto debería dar 63 posiciones disponibles
  };

  // Función para distribuir los pasos en las posiciones disponibles
  const distributeStepsInBoard = (steps) => {
    const availablePositions = getAvailablePositions();
    const totalAvailable = availablePositions.length; // 63
    const totalSteps = steps.length;
    
    if (totalSteps === 0) return [];
    
    const stepsWithPositions = steps.map((step, index) => {
      // Distribuir uniformemente: dividir el espacio total entre el número de pasos
      // Usar Math.round para mejor distribución y agregar un offset para evitar posición 0
      const positionIndex = Math.round((index * (totalAvailable - 1)) / (totalSteps - 1));
      
      // Asegurar que no exceda el límite
      const safePositionIndex = Math.min(positionIndex, totalAvailable - 1);
      const boardPosition = availablePositions[safePositionIndex];
      
      return {
        ...step,
        pa_posicion_tablero: boardPosition,
        pa_numero: index + 1 // Renumerar según el orden
      };
    });
    
    return stepsWithPositions;
  };

  useEffect(() => {
    fetchPasosTablero();
  }, []);

  const fetchPasosTablero = async () => {
    const userId = getUserId();
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/pasos-tablero/${userId}`, { 
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      
      // Distribuir los pasos dinámicamente en el tablero
      const stepsWithPositions = distributeStepsInBoard(data);
      setPasos(stepsWithPositions);

      const completedFromDB = stepsWithPositions
        .filter(step => step.uspa_completado === 1)
        .map(step => step.pa_numero);
      
      setCompletedSteps(completedFromDB);
      
      // Si hay pasos, el primer paso debe estar disponible
      if (stepsWithPositions.length > 0) {
        const nextStep = stepsWithPositions.find(step => step.uspa_completado !== 1);
        setCurrentStep(nextStep ? nextStep.pa_numero : stepsWithPositions.length + 1);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar los pasos del juego. Por favor, intenta de nuevo más tarde.');
      setLoading(false);
    }
  };

  const createSnakePattern = () => {
    const cells = [];
    let position = 0;
    for (let col = 0; col < gameConfig.cols; col++) {
      for (let row = 0; row < gameConfig.rows; row++) {
        const actualRow = col % 2 === 0 ? row : gameConfig.rows - 1 - row;
        if (!cells[actualRow]) cells[actualRow] = [];
        cells[actualRow][col] = position++;
      }
    }
    return cells;
  };

  const snakePattern = createSnakePattern();

  const isCellDisabled = (position) => gameConfig.disabledCells.includes(position);
  const getStepInfo = (position) => pasos.find(step => step.pa_posicion_tablero === position);
  const isStepPosition = (position) => pasos.some(step => step.pa_posicion_tablero === position);
  const isStepCompleted = (position) => {
    const stepInfo = getStepInfo(position);
    return stepInfo && stepInfo.uspa_completado === 1;
  };
  const isStepAvailable = (position) => {
    const stepInfo = getStepInfo(position);
    return stepInfo && stepInfo.pa_numero === currentStep;
  };

  const handleCellClick = (position) => {
    const stepInfo = getStepInfo(position);
    if (stepInfo) {
      const isCompleted = stepInfo.uspa_completado === 1;
      const isAvailable = stepInfo.pa_numero === currentStep;
      if (isCompleted || isAvailable){
        navigate(`/paso/${stepInfo.pa_clave}`);
      }
    }
  };

  return (
    <div className={styles.juegoContainer}>
      {/* Título con fondo */}
      <div className={styles.tituloContainer}>
        <h1 className={styles.gameTitle}>Tu Carrera Universitaria</h1>
      </div>

      {/* Barra de progreso */}
      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressBarFill}
            style={{ width: `${(completedSteps.length / pasos.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Tablero */}
      <div className={styles.board}>
        <div
          className={styles.gridContainer}
          style={{ gridTemplateColumns: `repeat(${gameConfig.cols}, 100px)` }}
        >
          {snakePattern.flat().map((position, index) => {
            const stepInfo = getStepInfo(position);
            const isHovered = hoveredCell === position;
            const hasStep = isStepPosition(position);
            const isDisabled = isCellDisabled(position);

            // Si la casilla está deshabilitada, renderizar celda deshabilitada
            if (isDisabled) {
              return (
                <div
                  key={`cell-${position}-${index}`}
                  className={`${styles.cell} ${styles.disabled}`}
                >
                  <span className={styles.position}>{position}</span>
                  <div className={styles.xIcon}>×</div>
                </div>
              );
            }

            // Si no tiene paso, renderizar celda invisible
            if (!hasStep) {
              return (
                <div
                  key={`cell-${position}-${index}`}
                  className={`${styles.cell} ${styles.invisible}`}
                />
              );
            }

            // Si tiene paso, renderizar normalmente
            return (
              <div
                key={`cell-${position}-${index}`}
                className={`${styles.cell} ${styles.step} ${
                  isStepCompleted(position) ? styles.completed : ''
                } ${isStepAvailable(position) ? styles.available : ''}`}
                onClick={() => handleCellClick(position)}
                onMouseEnter={() => setHoveredCell(position)}
                onMouseLeave={() => setHoveredCell(null)}
              >
                {/* Título del paso centrado */}
                <span className={styles.stepTitle}>{stepInfo.pa_titulo}</span>

                {/* Número del paso en círculo inferior izquierdo */}
                <div className={styles.stepNumberCircle}>
                  {stepInfo.pa_numero}
                </div>

                {/* Iconos de estado */}
                <div className={styles.stepIcon}>
                  {isStepCompleted(position) ? (
                    <CheckCircle size={20} color="white" />
                  ) : isStepAvailable(position) ? (
                    <Circle size={20} className={styles.pulse} color="white" />
                  ) : (
                    <Circle size={20} color="white" />
                  )}
                </div>

                {/* Tooltip mejorado */}
                {isHovered && stepInfo && (
                  <div className={styles.tooltipImproved}>
                    {stepInfo.pa_titulo}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JuegoCarrera;